import type { ReactNode } from 'react';

import { Loader2, Pause, Play, Square } from 'lucide-react';

import { Button } from '@muzly/ui-kit';

import { useSessionControl } from './sessionControlContext';
import { formatElapsed } from './sessionReconcile';

/**
 * 통합 세션 전송(transport) 제어 바(#25, #27).
 *
 * 모든 transport 제어를 한 곳에 모은다: 라이브 점 + 경과 타이머(mm:ss) +
 * 주 시작/정지 토글 + 보조 일시정지/재개(컴팩트). 풀 패널과 sticky 바가
 * 각자 정지 버튼을 갖던 중복을 없애고, 이 컴포넌트 하나가 transport를 소유한다.
 *
 * 콘솔이 탭으로 나뉘면서(#27) transport는 **전역 sticky 바 하나**로 통일한다 —
 * 어느 탭에 있어도 화면 하단에 항상 고정돼(safe-area inset) 시작/정지에 닿는다.
 *
 * 멀티클릭/전이중 잠금은 컨텍스트(`isTransitioning`)·상태별 `can`을 그대로 따른다.
 */

const SecondaryButton = ({
  label,
  icon,
  onClick,
  disabled,
}: {
  label: string;
  icon: ReactNode;
  onClick: () => void;
  disabled: boolean;
}) => (
  <Button
    size="icon"
    variant="secondary"
    className="h-12 w-12 shrink-0 transition-transform disabled:cursor-not-allowed motion-safe:enabled:active:scale-95"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
  >
    {icon}
  </Button>
);

const TransportControls = () => {
  const { liveness, can, isStopped, isTransitioning, run } =
    useSessionControl();

  const dotClass = liveness.live
    ? 'bg-green-500 animate-pulse'
    : liveness.stalled
      ? 'bg-destructive'
      : 'bg-muted-foreground/40';

  return (
    <div className="flex items-center gap-2">
      <div className="flex min-w-0 items-center gap-1.5">
        <span className={`inline-block h-2.5 w-2.5 rounded-full ${dotClass}`} />
        <span className="font-mono text-xs text-muted-foreground">
          {formatElapsed(liveness.elapsedMs)}
        </span>
      </div>

      {/* 주 토글: 대기=시작(초록)/실행·일시정지=정지(빨강). 전이 중 비활성+스피너. */}
      <Button
        size="lg"
        variant={isStopped ? 'default' : 'destructive'}
        className="h-12 flex-1 gap-2 text-base transition-transform disabled:cursor-not-allowed motion-safe:enabled:active:scale-[0.98]"
        disabled={isTransitioning}
        onClick={() => run(isStopped ? 'start' : 'stop')}
      >
        {isTransitioning ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" />
            처리 중…
          </>
        ) : isStopped ? (
          <>
            <Play className="h-5 w-5" />
            시작
          </>
        ) : (
          <>
            <Square className="h-5 w-5" />
            정지
          </>
        )}
      </Button>

      {/* 보조: 일시정지/재개(상태별 disable, 전이 중 잠금 유지). */}
      <SecondaryButton
        label="일시정지"
        icon={<Pause className="h-5 w-5" />}
        onClick={() => run('pause')}
        disabled={!can.pause}
      />
      <SecondaryButton
        label="재개"
        icon={<Play className="h-5 w-5" />}
        onClick={() => run('resume')}
        disabled={!can.resume}
      />
    </div>
  );
};

export const SessionTransport = () => (
  <div
    className="sticky bottom-0 z-30 -mx-4 border-t border-border/60 bg-background/95 px-4 pt-2.5 backdrop-blur"
    style={{
      paddingBottom:
        'calc(var(--safe-area-bottom, env(safe-area-inset-bottom, 0px)) + 0.625rem)',
    }}
  >
    <TransportControls />
  </div>
);
